import React, { useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { LayoutSingleColumn, Logo, ListingCard, Page } from '../../components';
import { createURLParamsForListing } from '../../util/listingHelpers';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import config from '../../config/configDefault';
import css from './GiftCardPage.module.css';

// Initialize Stripe
const stripePromise = loadStripe(config.stripe.publishableKey);

const CheckoutForm = ({ clientSecret, email, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const intl = useIntl();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Return URL is not strictly needed for card payments if we handle it here,
        // but required by Stripe. We can point to the current page.
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      setMessage(error.message);
      setIsLoading(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setMessage(intl.formatMessage({ id: 'GiftCardPage.errorUnexpectedState' }));
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={css.paymentForm}>
      <PaymentElement />
      <button disabled={isLoading || !stripe || !elements} id="submit" className={css.payButton}>
        <span id="button-text">
          {isLoading ? (
            <div className="spinner" id="spinner">
              <FormattedMessage id="GiftCardPage.processing" defaultMessage="Processing..." />
            </div>
          ) : (
            <FormattedMessage id="GiftCardPage.payNow" defaultMessage="Pay now" />
          )}
        </span>
      </button>
      {message && <div id="payment-message" className={css.errorMessage}>{message}</div>}
    </form>
  );
};

const GiftCardPage = () => {
  const intl = useIntl();
  const location = useLocation();
  console.log('GiftCardPage location:', location);
  console.log('GiftCardPage location.state:', location.state);
  const suggestedListing = location?.state?.suggestedListing;

  // Ensure listing has minimal required data to avoid ListingCard crashing
  // and handle potential UUID serialization issues
  const validListing = useMemo(() => {
    if (!suggestedListing || !suggestedListing.id || !suggestedListing.attributes) {
      return null;
    }
    return suggestedListing;
  }, [suggestedListing]);

  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [currency, setCurrency] = useState('CHF');
  
  // Email State
  const [purchaserEmail, setPurchaserEmail] = useState('');
  const [sendToRecipient, setSendToRecipient] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [confirmRecipientEmail, setConfirmRecipientEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [message, setMessage] = useState('');

  const [clientSecret, setClientSecret] = useState('');
  const [step, setStep] = useState('selection'); // selection, payment, success
  const [purchasedCode, setPurchasedCode] = useState(null);
  const [error, setError] = useState(null);

  const handleInitiate = async () => {
    if (!purchaserEmail) {
      setError(intl.formatMessage({ id: 'GiftCardPage.errorEmailRequired' }));
      return;
    }

    if (!fromName) {
      setError(intl.formatMessage({ id: 'GiftCardPage.errorNameRequired' }));
      return;
    }

    if (sendToRecipient) {
      if (!recipientEmail) {
        setError(intl.formatMessage({ id: 'GiftCardPage.errorRecipientEmailRequired' }));
        return;
      }
      if (recipientEmail !== confirmRecipientEmail) {
        setError(intl.formatMessage({ id: 'GiftCardPage.errorEmailMismatch' }));
        return;
      }
    }
    
    const finalAmount = amount === 'custom' ? parseFloat(customAmount) : amount;
    if (!finalAmount || finalAmount < 1) {
      setError(intl.formatMessage({ id: 'GiftCardPage.errorInvalidAmount' }));
      return;
    }

    setError(null);
    
    try {
      const response = await fetch('/api/gift-card/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          amount: Math.round(finalAmount * 100), // Convert to cents
          currency: currency, 
          email: purchaserEmail 
        }),
      });
      
      const data = await response.json();
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setStep('payment');
      } else {
        setError(intl.formatMessage({ id: 'GiftCardPage.errorInitiatePayment' }));
      }
    } catch (e) {
      setError(intl.formatMessage({ id: 'GiftCardPage.errorNetwork' }));
    }
  };

  const handleSuccess = async (paymentIntentId) => {
    try {
      // Prepare simplified listing object for email
      let simplifiedListing = null;
      if (validListing) {
        const urlParams = createURLParamsForListing(validListing, intl.locale, intl);
        const firstImage = validListing.images && validListing.images.length > 0 ? validListing.images[0] : null;
        const imageUrl = firstImage?.attributes?.variants?.['landscape-crop']?.url || 
                         firstImage?.attributes?.variants?.['default']?.url || null;
        
        simplifiedListing = {
          id: validListing.id.uuid,
          slug: urlParams.slug,
          type: urlParams.type,
          title: validListing.attributes.title,
          imageUrl: imageUrl
        };
      }

      const response = await fetch('/api/gift-card/finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          paymentIntentId, 
          purchaserEmail,
          recipientEmail: sendToRecipient ? recipientEmail : null,
          sendToRecipient,
          fromName,
          message,
          locale: intl.locale, // Send current locale to backend
          suggestedListing: simplifiedListing
        }),
      });
      
      const data = await response.json();
      if (data.success) {
        setPurchasedCode(data.code);
        setStep('success');
      } else {
        setError(intl.formatMessage({ id: 'GiftCardPage.errorVoucherCreation' }));
      }
    } catch (e) {
      setError(intl.formatMessage({ id: 'GiftCardPage.errorFinalization' }));
    }
  };

  return (
    <Page title={intl.formatMessage({ id: 'GiftCardPage.pageTitle', defaultMessage: 'Buy a Gift Card' })}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
      <div className={css.root}>
        <div className={css.content}>
          
          <div className={css.hero}>
            <h1 className={css.heroTitle}>
              <FormattedMessage id="GiftCardPage.heroTitle" defaultMessage="Give the Gift of Adventure" />
            </h1>
            <p className={css.heroSubtitle}>
              <FormattedMessage id="GiftCardPage.heroSubtitle" defaultMessage="The perfect present for outdoor enthusiasts. Valid for thousands of activities." />
            </p>
          </div>

          <div className={css.mainGrid}>
            {/* Left Column: Form */}
            <div className={css.formContainer}>
              {step === 'selection' && (
                <>
                  <h2 className={css.sectionTitle}>
                    <FormattedMessage id="GiftCardPage.configureTitle" defaultMessage="Configure your Gift" />
                  </h2>
                  
                  <div className={css.currencySelector}>
                    <label className={css.label}>
                      <FormattedMessage id="GiftCardPage.currencyLabel" defaultMessage="Currency" />
                    </label>
                    <div className={css.currencyButtons}>
                      {['CHF', 'EUR'].map(curr => (
                        <button
                          key={curr}
                          className={`${css.amountButton} ${currency === curr ? css.selected : ''}`}
                          onClick={() => setCurrency(curr)}
                        >
                          {curr}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={css.amountGrid}>
                    {[50, 100, 200, 500].map(val => (
                      <button 
                        key={val}
                        className={`${css.amountButton} ${amount === val ? css.selected : ''}`}
                        onClick={() => { setAmount(val); setCustomAmount(''); }}
                      >
                        {val} {currency}
                      </button>
                    ))}
                    <button 
                      className={`${css.amountButton} ${amount === 'custom' ? css.selected : ''}`}
                      onClick={() => setAmount('custom')}
                    >
                      <FormattedMessage id="GiftCardPage.customAmount" defaultMessage="Custom" />
                    </button>
                  </div>

                  {amount === 'custom' && (
                    <div className={css.customInputWrapper}>
                      <label className={css.label}>
                        <FormattedMessage id="GiftCardPage.enterAmountLabel" defaultMessage="Enter Amount" /> ({currency})
                      </label>
                      <input 
                        type="number" 
                        value={customAmount} 
                        onChange={(e) => setCustomAmount(e.target.value)}
                        className={css.input}
                        placeholder={intl.formatMessage({ id: 'GiftCardPage.amountPlaceholder', defaultMessage: 'e.g. 150' })}
                      />
                    </div>
                  )}

                  <div className={css.emailWrapper}>
                    <label className={css.label}>
                      <FormattedMessage id="GiftCardPage.purchaserEmailLabel" defaultMessage="Your Email" />
                    </label>
                    <input 
                      type="email" 
                      value={purchaserEmail} 
                      onChange={(e) => setPurchaserEmail(e.target.value)}
                      className={css.input}
                      placeholder={intl.formatMessage({ id: 'GiftCardPage.emailPlaceholder', defaultMessage: 'you@example.com' })}
                    />
                  </div>

                  <div className={css.emailWrapper}>
                    <label className={css.label}>
                      <FormattedMessage id="GiftCardPage.fromNameLabel" defaultMessage="Your Name" />
                    </label>
                    <input  
                      type="text" 
                      value={fromName} 
                      onChange={(e) => setFromName(e.target.value)}
                      className={css.input}
                      placeholder={intl.formatMessage({ id: 'GiftCardPage.fromNamePlaceholder', defaultMessage: 'e.g. John Doe' })}
                    />
                  </div>

                  <div className={css.emailWrapper}>
                    <label className={css.label}>
                      <FormattedMessage id="GiftCardPage.messageLabel" defaultMessage="Message (Optional)" />
                    </label>
                    <textarea 
                      value={message} 
                      onChange={(e) => setMessage(e.target.value)}
                      className={css.input}
                      rows={3}
                      placeholder={intl.formatMessage({ id: 'GiftCardPage.messagePlaceholder', defaultMessage: 'Write a personal note...' })}
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  <div className={css.checkboxWrapper} style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={sendToRecipient} 
                        onChange={(e) => setSendToRecipient(e.target.checked)}
                        style={{ marginRight: '10px', width: '20px', height: '20px' }}
                      />
                      <span className={css.label} style={{ marginBottom: 0 }}>
                        <FormattedMessage id="GiftCardPage.sendToRecipientLabel" defaultMessage="Send gift card to recipient via email?" />
                      </span>
                    </label>
                  </div>

                  {sendToRecipient && (
                    <>
                      <div className={css.emailWrapper}>
                        <label className={css.label}>
                          <FormattedMessage id="GiftCardPage.recipientEmailLabel" defaultMessage="Recipient Email" />
                        </label>
                        <input 
                          type="email" 
                          value={recipientEmail} 
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          className={css.input}
                          placeholder={intl.formatMessage({ id: 'GiftCardPage.emailPlaceholder', defaultMessage: 'friend@example.com' })}
                        />
                      </div>
                      <div className={css.emailWrapper}>
                        <label className={css.label}>
                          <FormattedMessage id="GiftCardPage.confirmRecipientEmailLabel" defaultMessage="Confirm Recipient Email" />
                        </label>
                        <input 
                          type="email" 
                          value={confirmRecipientEmail} 
                          onChange={(e) => setConfirmRecipientEmail(e.target.value)}
                          className={css.input}
                          placeholder={intl.formatMessage({ id: 'GiftCardPage.emailPlaceholder', defaultMessage: 'friend@example.com' })}
                        />
                      </div>
                    </>
                  )}

                  {error && <div className={css.errorMessage}>{error}</div>}

                  <button className={css.primaryButton} onClick={handleInitiate}>
                    <FormattedMessage id="GiftCardPage.proceedButton" defaultMessage="Proceed to Payment" />
                  </button>
                </>
              )}

              {step === 'payment' && clientSecret && (
                <div className={css.paymentContainer}>
                  <h2>
                    <FormattedMessage id="GiftCardPage.paymentDetailsTitle" defaultMessage="Payment Details" />
                  </h2>
                  <p>
                    <FormattedMessage 
                      id="GiftCardPage.amountLabel" 
                      defaultMessage="Amount: {amount} {currency}" 
                      values={{ 
                        amount: amount === 'custom' ? customAmount : amount,
                        currency: currency
                      }}
                    />
                  </p>
                  <Elements options={{ clientSecret, appearance: { theme: 'stripe' }, locale: intl.locale }} stripe={stripePromise}>
                    <CheckoutForm clientSecret={clientSecret} email={purchaserEmail} onSuccess={handleSuccess} />
                  </Elements>
                  <button className={css.backButton} onClick={() => setStep('selection')}>
                    <FormattedMessage id="GiftCardPage.backButton" defaultMessage="Back" />
                  </button>
                </div>
              )}

              {step === 'success' && (
                <div className={css.successContainer}>
                  <h2 className={css.successTitle}>
                    <FormattedMessage id="GiftCardPage.successTitle" defaultMessage="Success!" />
                  </h2>
                  <p>
                    <FormattedMessage id="GiftCardPage.successMessage" defaultMessage="Your gift card has been created." />
                  </p>
                  <div className={css.codeDisplay}>
                    {purchasedCode}
                  </div>
                  <p>
                    <FormattedMessage 
                      id="GiftCardPage.emailSentMessage" 
                      defaultMessage="We have sent an email to {email} with the code." 
                      values={{ email: <strong>{purchaserEmail}</strong> }}
                    />
                  </p>
                  {sendToRecipient && (
                    <p>
                      <FormattedMessage 
                        id="GiftCardPage.recipientEmailSentMessage" 
                        defaultMessage="We also sent a copy to {email}." 
                        values={{ email: <strong>{recipientEmail}</strong> }}
                      />
                    </p>
                  )}
                  <button className={css.primaryButton} onClick={() => {
                    setStep('selection');
                    setPurchasedCode(null);
                    setClientSecret('');
                    setPurchaserEmail('');
                    setRecipientEmail('');
                    setConfirmRecipientEmail('');
                    setSendToRecipient(false);
                  }}>
                    <FormattedMessage id="GiftCardPage.buyAnotherButton" defaultMessage="Buy Another" />
                  </button>
                </div>
              )}
            </div>

            {/* Right Column: Preview & Info */}
            <div className={css.infoContainer}>
              <div className={css.cardPreview}>
                <div className={css.cardPattern}></div>
                <div className={css.cardHeader}>
                  <Logo className={css.logo} format="desktop" />
                  <span className={css.cardLabel}>Gift Card</span>
                </div>
                <div className={css.cardBody}>
                  <div className={css.cardAmount}>
                    {amount === 'custom' ? (customAmount || '0') : amount} {currency}
                  </div>
                  {(fromName || message || validListing) && (
                    <div style={{ marginTop: '20px', fontSize: '14px', opacity: 0.9 }}>
                      {fromName && <div><strong><FormattedMessage id="GiftCardPage.fromLabel" defaultMessage="From:" /></strong> {fromName}</div>}
                      {message && <div style={{ marginTop: '8px', fontStyle: 'italic' }}>"{message}"</div>}
                    </div>
                  )}
                </div>
                <div className={css.cardFooter}>
                  <span className={css.cardText}>
                    <FormattedMessage id="GiftCardPage.validityText" defaultMessage="Valid for 1 year" />
                  </span>
                </div>
              </div>

              <ul className={css.featuresList}>
                <li className={css.featureItem}>
                  <span className={css.checkIcon}>✓</span>
                  <FormattedMessage id="GiftCardPage.feature1" defaultMessage="Valid for all activities on Crion" />
                </li>
                <li className={css.featureItem}>
                  <span className={css.checkIcon}>✓</span>
                  <FormattedMessage id="GiftCardPage.feature2" defaultMessage="Instant email delivery" />
                </li>
                <li className={css.featureItem}>
                  <span className={css.checkIcon}>✓</span>
                  <FormattedMessage id="GiftCardPage.feature3" defaultMessage="Secure payment via Stripe" />
                </li>
              </ul>

              {validListing && (
                <div className={css.suggestedListingWrapper}>
                  <h3 className={css.suggestedListingTitle}>
                    <FormattedMessage id="GiftCardPage.suggestedAdventure" defaultMessage="Suggested Adventure:" />
                  </h3>
                  <ListingCard listing={validListing} />
                </div>
              )}
            </div>
          </div>

          {/* How it works */}
          <div className={css.howItWorks}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="GiftCardPage.howItWorksTitle" defaultMessage="How it works" />
            </h2>
            <div className={css.stepsGrid}>
              <div className={css.step}>
                <div className={css.stepIcon}>1</div>
                <h3 className={css.stepTitle}>
                  <FormattedMessage id="GiftCardPage.step1Title" defaultMessage="Choose Amount" />
                </h3>
                <p className={css.stepDesc}>
                  <FormattedMessage id="GiftCardPage.step1Desc" defaultMessage="Select a value that fits your budget and enter the recipient's email." />
                </p>
              </div>
              <div className={css.step}>
                <div className={css.stepIcon}>2</div>
                <h3 className={css.stepTitle}>
                  <FormattedMessage id="GiftCardPage.step2Title" defaultMessage="Receive Code" />
                </h3>
                <p className={css.stepDesc}>
                  <FormattedMessage id="GiftCardPage.step2Desc" defaultMessage="We send a unique voucher code directly to the recipient via email." />
                </p>
              </div>
              <div className={css.step}>
                <div className={css.stepIcon}>3</div>
                <h3 className={css.stepTitle}>
                  <FormattedMessage id="GiftCardPage.step3Title" defaultMessage="Book Adventure" />
                </h3>
                <p className={css.stepDesc}>
                  <FormattedMessage id="GiftCardPage.step3Desc" defaultMessage="They can use the code to book any activity on our marketplace." />
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </LayoutSingleColumn>
    </Page>
  );
};

export default GiftCardPage;
